import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import netlifyIdentity from "netlify-identity-widget";
import {
  getContentfulClient,
  hasContentfulConfig,
  hasContentfulPreviewConfig,
} from "../../lib/contentful";

const getLocalizedValue = (value, preferredLocales) => {
  if (!value) return value;
  if (value.nodeType || value.sys) return value;
  if (Array.isArray(value)) return value;
  if (typeof value === "object") {
    if (preferredLocales && preferredLocales.length) {
      for (const locale of preferredLocales) {
        if (value[locale]) return value[locale];
      }
    }
    const keys = Object.keys(value);
    if (keys.length) return value[keys[0]];
  }
  return value;
};

const normalizeAssetUrl = (url) => {
  if (!url) return "";
  return url.startsWith("//") ? `https:${url}` : url;
};

const getAssetUrl = (asset, preferredLocales) => {
  if (!asset || !asset.fields) return "";
  const fileField = asset.fields.file;
  if (!fileField) return "";
  if (fileField.url) return normalizeAssetUrl(fileField.url);
  const localizedFile = getLocalizedValue(fileField, preferredLocales);
  return localizedFile && localizedFile.url
    ? normalizeAssetUrl(localizedFile.url)
    : "";
};

const getCategoryLabel = (entry, preferredLocales) => {
  if (!entry) return "";
  const fields = entry.fields || {};
  return (
    getLocalizedValue(fields.name, preferredLocales) ||
    getLocalizedValue(fields.title, preferredLocales) ||
    getLocalizedValue(fields.internalName, preferredLocales) ||
    ""
  );
};

const getCategorySlug = (entry, preferredLocales) => {
  if (!entry) return "";
  const fields = entry.fields || {};
  const slug =
    getLocalizedValue(fields.slug, preferredLocales) ||
    getLocalizedValue(fields.name, preferredLocales) ||
    getLocalizedValue(fields.title, preferredLocales) ||
    getLocalizedValue(fields.internalName, preferredLocales) ||
    "";
  return slug
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [assetsById, setAssetsById] = useState({});
  const [entriesById, setEntriesById] = useState({});
  const [status, setStatus] = useState("idle");
  const [isPreview, setIsPreview] = useState(false);
  const [user, setUser] = useState(null);
  const [activeCategory, setActiveCategory] = useState("");
  const preferredLocales = useMemo(() => {
    const raw =
      process.env.REACT_APP_CONTENTFUL_LOCALES ||
      process.env.REACT_APP_CONTENTFUL_LOCALE ||
      "en-US";
    return raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const params = new URLSearchParams(window.location.search);
    const previewEnabled = params.get("preview") === "1";
    const categoryParam = params.get("category") || "";
    setIsPreview(previewEnabled);
    setActiveCategory(categoryParam);

    const fetchPosts = async () => {
      if (!hasContentfulConfig) {
        setStatus("missing-config");
        return;
      }

      setStatus("loading");
      try {
        const client = getContentfulClient(previewEnabled);
        if (!client) {
          setStatus("missing-config");
          return;
        }

        const clientWithLocales =
          client && typeof client.withAllLocales === "function"
            ? client.withAllLocales()
            : client;

        const response = await clientWithLocales.getEntries({
          content_type: "pageBlogPost",
          order: "-fields.publishedDate",
          include: 2,
        });

        if (isMounted) {
          const items = response.items || [];
          const assets =
            response.includes && response.includes.Asset
              ? response.includes.Asset
              : [];
          const mappedAssets = assets.reduce((acc, asset) => {
            acc[asset.sys.id] = asset;
            return acc;
          }, {});
          const entries =
            response.includes && response.includes.Entry
              ? response.includes.Entry
              : [];
          const mappedEntries = entries.reduce((acc, entry) => {
            acc[entry.sys.id] = entry;
            return acc;
          }, {});
          setAssetsById(mappedAssets);
          setEntriesById(mappedEntries);
          if (categoryParam) {
            const filtered = items.filter((post) => {
              const categories = Array.isArray(post.fields.categories)
                ? post.fields.categories
                : [];
              return categories.some((category) => {
                const resolvedCategory =
                  category && !category.fields
                    ? mappedEntries[category.sys?.id]
                    : category;
                const slug = getCategorySlug(resolvedCategory, preferredLocales);
                return slug === categoryParam;
              });
            });
            setPosts(filtered);
          } else {
            setPosts(items);
          }
          setStatus("ready");
        }
      } catch (error) {
        console.error("Contentful fetch error:", error);
        if (isMounted) setStatus("error");
      }
    };

    fetchPosts();
    return () => {
      isMounted = false;
    };
  }, [preferredLocales]);

  useEffect(() => {
    netlifyIdentity.init();
    setUser(netlifyIdentity.currentUser());
    const handleLogin = (loggedInUser) => setUser(loggedInUser);
    const handleLogout = () => setUser(null);
    netlifyIdentity.on("login", handleLogin);
    netlifyIdentity.on("logout", handleLogout);
    return () => {
      netlifyIdentity.off("login", handleLogin);
      netlifyIdentity.off("logout", handleLogout);
    };
  }, []);

  const handleDelete = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const response = await fetch("/.netlify/functions/deletePost", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token?.access_token || ""}`,
        },
        body: JSON.stringify({ slug }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Delete failed.");
      }
      setPosts((prev) => prev.filter((post) => post.fields.slug !== slug));
    } catch (error) {
      console.error(error);
      alert("Delete failed.");
    }
  };

  return (
    <section className="blog-section">
      <article className="blog-hero">
        <h1>Blog</h1>
        <p>Notes on projects, experiments, and things I am learning.</p>
      </article>

      {isPreview && hasContentfulPreviewConfig && (
        <article className="blog-card">
          <p>Preview mode is enabled.</p>
        </article>
      )}

      {!hasContentfulConfig && (
        <article className="blog-card">
          <h2>Contentful is not configured</h2>
          <p>
            Add your Contentful Space ID and Delivery token in environment
            variables to load posts.
          </p>
        </article>
      )}

      {isPreview && !hasContentfulPreviewConfig && (
        <article className="blog-card">
          <p>Preview is enabled, but no preview token is configured.</p>
        </article>
      )}

      {status === "loading" && (
        <article className="blog-card">
          <p>Loading posts...</p>
        </article>
      )}

      {status === "error" && (
        <article className="blog-card">
          <p>Could not load posts right now.</p>
        </article>
      )}

      {status === "ready" && posts.length === 0 && (
        <article className="blog-card">
          <p>No posts yet.</p>
        </article>
      )}

      {status === "ready" && posts.length > 0 && (
        <div className="blog-grid">
          {posts.map((post) => {
            const title = getLocalizedValue(
              post.fields.title,
              preferredLocales
            );
            const subtitle = getLocalizedValue(
              post.fields.shortDescription,
              preferredLocales
            );
            const slug = getLocalizedValue(
              post.fields.slug,
              preferredLocales
            );
            const featuredImage = post.fields.featuredImage;
            const resolvedImage =
              featuredImage && !featuredImage.fields
                ? assetsById[featuredImage.sys?.id]
                : featuredImage;
            const imageUrl = getAssetUrl(resolvedImage, preferredLocales);
            const publishedDate = post.fields.publishedDate
              ? new Date(
                  getLocalizedValue(
                    post.fields.publishedDate,
                    preferredLocales
                  )
                ).toLocaleDateString()
              : "";
            const categories = Array.isArray(post.fields.categories)
              ? post.fields.categories
              : [];
            const categoryLabels = categories
              .map((category) => {
                const resolvedCategory =
                  category && !category.fields
                    ? entriesById[category.sys?.id]
                    : category;
                return {
                  label: getCategoryLabel(resolvedCategory, preferredLocales),
                  slug: getCategorySlug(resolvedCategory, preferredLocales),
                };
              })
              .filter((item) => item.label);

            return (
              <article className="blog-card" key={post.sys.id}>
                {imageUrl && (
                  <Link to={`/blog/${slug}`} className="blog-image-link">
                    <img
                      className="blog-image"
                      src={imageUrl}
                      alt={title || "Blog post"}
                    />
                  </Link>
                )}
                <div className="blog-card-content">
                  {publishedDate && (
                    <p className="blog-meta">{publishedDate}</p>
                  )}
                  {categoryLabels.length > 0 && (
                    <div className="blog-categories">
                      {categoryLabels.map((category) => (
                        <Link
                          key={`${category.slug}-${category.label}`}
                          to={`/blog?category=${category.slug}`}
                          className={`blog-category-chip${
                            activeCategory === category.slug ? " active" : ""
                          }`}
                        >
                          {category.label}
                        </Link>
                      ))}
                    </div>
                  )}
                  <h2 className="blog-title">
                    <Link to={`/blog/${slug}`}>{title}</Link>
                  </h2>
                  {subtitle && <p className="blog-subtitle">{subtitle}</p>}
                  <Link to={`/blog/${slug}`} className="blog-read-more">
                    Read more →
                  </Link>
                  {user && (
                    <div className="blog-admin-actions">
                      <Link to={`/admin?edit=${slug}`}>Edit</Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(slug)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default Blog;
